import { LegalLayout } from "@/components/legal/LegalLayout";

export function TermsPage() {
  return (
    <LegalLayout title="Terms of Service" lastUpdated="February 14, 2026">
      <nav>
        <p className="!text-foreground !font-medium !mb-4">Table of Contents</p>
        <ol>
          <li><a href="#acceptance">Acceptance of Terms</a></li>
          <li><a href="#description">Description of Service</a></li>
          <li><a href="#accounts">User Accounts</a></li>
          <li><a href="#acceptable-use">Acceptable Use</a></li>
          <li><a href="#intellectual-property">Intellectual Property</a></li>
          <li><a href="#ai-content">AI-Generated Content</a></li>
          <li><a href="#payment">Payment & Credits</a></li>
          <li><a href="#liability">Limitation of Liability</a></li>
          <li><a href="#termination">Termination</a></li>
          <li><a href="#changes">Changes to Terms</a></li>
          <li><a href="#governing-law">Governing Law</a></li>
          <li><a href="#contact">Contact</a></li>
        </ol>
      </nav>

      <section>
        <h2 id="acceptance">1. Acceptance of Terms</h2>
        <p>
          By accessing or using Rayloop AI ("the Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, you may not access or use the Service. These Terms constitute a legally binding agreement between you and Rayloop AI, Inc. ("Rayloop," "we," "us," or "our").
        </p>
        <p>
          By creating an account, clicking "I agree," or otherwise accessing the Service, you acknowledge that you have read, understood, and agree to be bound by these Terms, as well as our <a href="/privacy">Privacy Policy</a> and <a href="/cookies">Cookie Policy</a>.
        </p>
      </section>

      <section>
        <h2 id="description">2. Description of Service</h2>
        <p>
          Rayloop AI is a visual workflow builder for chaining AI models into reproducible content creation pipelines. The Service allows users to build node-based graphs where each node represents an AI operation — including text generation, image generation, video generation, and audio generation — and edges define the data flow between operations.
        </p>
        <p>
          The Service provides a web-based interface for designing, executing, and managing these pipeline workflows. Features may include, but are not limited to: a drag-and-drop canvas editor, AI-powered chat assistance for building pipelines, real-time pipeline execution, template libraries, collaboration tools, and integrations with third-party AI model providers.
        </p>
        <p>
          We reserve the right to modify, suspend, or discontinue any aspect of the Service at any time, with or without notice.
        </p>
      </section>

      <section>
        <h2 id="accounts">3. User Accounts</h2>
        <p>
          To access certain features of the Service, you must create an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
        </p>
        <p>
          You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to notify us immediately of any unauthorized use of your account or any other security breach.
        </p>
        <p>
          You must be at least 18 years of age, or the age of legal majority in your jurisdiction, to create an account and use the Service. By creating an account, you represent and warrant that you meet this age requirement.
        </p>
      </section>

      <section>
        <h2 id="acceptable-use">4. Acceptable Use</h2>
        <p>
          You agree to use the Service only for lawful purposes and in accordance with these Terms. You agree not to use the Service:
        </p>
        <ul>
          <li>To generate, distribute, or facilitate the creation of content that is illegal, harmful, threatening, abusive, defamatory, obscene, or otherwise objectionable.</li>
          <li>To infringe upon or violate the intellectual property rights or privacy rights of any third party.</li>
          <li>To attempt to gain unauthorized access to any part of the Service, other accounts, computer systems, or networks connected to the Service.</li>
          <li>To interfere with or disrupt the Service or servers or networks connected to the Service.</li>
          <li>To use automated means (bots, scrapers, or similar tools) to access the Service without our express written permission.</li>
          <li>To bypass, circumvent, or attempt to bypass or circumvent any usage limits, rate limits, or credit restrictions imposed by the Service.</li>
          <li>To generate content that violates the acceptable use policies of the underlying AI model providers integrated with the Service.</li>
        </ul>
      </section>

      <section>
        <h2 id="intellectual-property">5. Intellectual Property</h2>
        <p>
          The Service, including its original content (excluding user-generated and AI-generated content), features, and functionality, is and will remain the exclusive property of Rayloop AI, Inc. and its licensors. The Service is protected by copyright, trademark, and other laws of both the United States and foreign countries.
        </p>
        <p>
          Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of Rayloop AI, Inc. All pipeline templates, UI components, and documentation provided as part of the Service are proprietary to Rayloop AI.
        </p>
      </section>

      <section>
        <h2 id="ai-content">6. AI-Generated Content</h2>
        <p>
          Content generated through the Service using AI models ("AI-Generated Content") is produced by third-party AI systems integrated into pipeline workflows you design. Subject to your compliance with these Terms and the rights of any applicable third-party AI providers, you retain ownership of the pipeline workflows you create and the outputs they produce.
        </p>
        <p>
          You acknowledge that AI-Generated Content may not be unique and that other users may receive similar or identical outputs. Rayloop AI makes no representations or warranties regarding the accuracy, completeness, originality, or fitness for a particular purpose of any AI-Generated Content.
        </p>
        <p>
          You are solely responsible for reviewing, verifying, and ensuring the appropriateness of all AI-Generated Content before distributing, publishing, or otherwise using it. You agree to comply with all applicable laws and regulations regarding the disclosure and labeling of AI-generated material.
        </p>
      </section>

      <section>
        <h2 id="payment">7. Payment & Credits</h2>
        <p>
          Certain features of the Service require the purchase of credits or a paid subscription plan. All fees are stated in U.S. dollars and are non-refundable except as expressly set forth in these Terms or as required by applicable law.
        </p>
        <p>
          Credits are consumed when pipeline workflows are executed. The credit cost of each execution depends on the AI models and operations used within the pipeline. Credit consumption rates are published on our pricing page and may be updated from time to time.
        </p>
        <p>
          Subscription plans renew automatically at the end of each billing period unless cancelled before the renewal date. You may cancel your subscription at any time through your account settings, and the cancellation will take effect at the end of the current billing period.
        </p>
        <p>
          We reserve the right to change our pricing at any time. For existing subscribers, price changes will take effect at the start of the next billing period following notice of the change.
        </p>
      </section>

      <section>
        <h2 id="liability">8. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL RAYLOOP AI, INC., ITS DIRECTORS, EMPLOYEES, PARTNERS, AGENTS, SUPPLIERS, OR AFFILIATES, BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING WITHOUT LIMITATION, LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM:
        </p>
        <ul>
          <li>Your access to or use of or inability to access or use the Service.</li>
          <li>Any conduct or content of any third party on the Service, including AI model providers.</li>
          <li>Any AI-Generated Content obtained from the Service.</li>
          <li>Unauthorized access, use, or alteration of your transmissions, pipeline workflows, or content.</li>
          <li>Errors, inaccuracies, or omissions in AI-Generated Content produced by pipeline workflows.</li>
        </ul>
        <p>
          Our total aggregate liability to you for all claims arising out of or relating to these Terms or the Service shall not exceed the greater of (a) the amount you have paid us in the twelve (12) months preceding the claim, or (b) one hundred U.S. dollars ($100).
        </p>
      </section>

      <section>
        <h2 id="termination">9. Termination</h2>
        <p>
          We may terminate or suspend your account and access to the Service immediately, without prior notice or liability, for any reason, including without limitation if you breach these Terms.
        </p>
        <p>
          Upon termination, your right to use the Service will immediately cease. If you wish to terminate your account, you may do so through your account settings or by contacting us. Any unused credits at the time of termination are non-refundable unless otherwise required by applicable law.
        </p>
        <p>
          We will make reasonable efforts to allow you to export your pipeline workflows and associated data for a period of thirty (30) days following termination, after which your data may be permanently deleted.
        </p>
      </section>

      <section>
        <h2 id="changes">10. Changes to Terms</h2>
        <p>
          We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will provide at least thirty (30) days' notice prior to any new terms taking effect, by posting the updated Terms on this page and updating the "Last updated" date above.
        </p>
        <p>
          Your continued use of the Service after any such changes constitutes your acceptance of the new Terms. If you do not agree to the new terms, you must stop using the Service.
        </p>
      </section>

      <section>
        <h2 id="governing-law">11. Governing Law</h2>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of the State of Delaware, United States, without regard to its conflict of law provisions. Any disputes arising under or in connection with these Terms shall be subject to the exclusive jurisdiction of the courts located in Wilmington, Delaware.
        </p>
        <p>
          Any dispute arising from or relating to the subject matter of these Terms that cannot be resolved through good-faith negotiation shall be finally settled by binding arbitration in accordance with the rules of the American Arbitration Association, with the arbitration taking place in Wilmington, Delaware.
        </p>
      </section>

      <section>
        <h2 id="contact">12. Contact</h2>
        <p>
          If you have any questions about these Terms, please contact us at:
        </p>
        <p>
          Rayloop AI, Inc.<br />
          Email: hello@rayloop.ai<br />
          Website: <a href="https://rayloop.ai">rayloop.ai</a>
        </p>
      </section>
    </LegalLayout>
  );
}
